import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles, ArrowRight, CheckCircle2, Hotel, Building2, Trees, Mountain,
    TrendingUp, Calculator, Award, Calendar, Target, MapPin, Clock,
    Banknote, Percent, ChevronRight, Send,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { api, fmtFJD } from "@/lib/api";

const PROJECT_TYPES = [
    {
        slug: "resort", icon: Hotel, title: "Beachfront Resort",
        body: "10-50 keys, full F&B, spa, water sports. Maximum exposure to Fiji's Tourism Hotel Aid Act incentives.",
        tag: "Tourism Hotel Aid Act eligible",
    },
    {
        slug: "boutique", icon: Trees, title: "Eco-Lodge / Boutique Stay",
        body: "Small-footprint, low-impact, premium-priced. Operating margins above 40%.",
        tag: "SLIP eligible (USD 250k+)",
    },
    {
        slug: "subdivision", icon: Building2, title: "Residential Subdivision",
        body: "Lot subdivision for foreign retirees, expat housing, holiday villas.",
        tag: "Capital Investment Incentive",
    },
    {
        slug: "destination", icon: Mountain, title: "Destination Development",
        body: "Multi-asset villages — resort + retail + adventure. USD 5m+ tickets.",
        tag: "Custom incentive packages",
    },
];

const INCENTIVES = [
    {
        title: "Tourism Hotel Aid Act",
        eyebrow: "10-25 year tax holiday",
        body: "New hotels & resorts with capital expenditure of FJD 250,000+ qualify for income tax holidays of 10 to 25 years depending on size and location. Outer-island and rural projects get the longest holidays.",
        bullets: [
            "Up to 25 years 0% income tax for qualifying outer-island resorts",
            "Import duty exemption on capital equipment, building materials, F&B inventory",
            "VAT zero-rating on qualifying construction inputs",
            "Accelerated depreciation: 100% write-off of buildings in year of completion",
        ],
    },
    {
        title: "Short-Life Investment Package (SLIP)",
        eyebrow: "Fast-track approvals + tax breaks",
        body: "For tourism, agriculture, and renewable energy projects. SLIP fast-tracks Investment Fiji approvals while bundling material tax concessions for projects between FJD 250k and FJD 7m.",
        bullets: [
            "5-7 year income tax holiday at FJD 250k–7m capex",
            "Customs duty concessions on capital plant",
            "Streamlined Investment Fiji review",
            "Compatible with Hotel Aid Act for hospitality",
        ],
    },
    {
        title: "Capital Investment Incentive Act",
        eyebrow: "Subdivisions & residential",
        body: "Residential subdivisions and large-scale housing developments qualify for substantial tax concessions, especially when targeting affordable housing or expat retiree segments.",
        bullets: [
            "Tax holidays up to 13 years for qualifying subdivisions",
            "Stamp duty exemptions on first transfers",
            "Reduced FRCS withholding tax on dividends repatriated",
            "Eligibility from FJD 1m project capex",
        ],
    },
    {
        title: "Foreign Investor Specific Concessions",
        eyebrow: "Repatriation & FX",
        body: "Once Reserve Bank of Fiji and FIRCA approvals are in place, foreign-equity tourism investors enjoy clean profit-repatriation pathways with minimal FX restrictions.",
        bullets: [
            "Full profit + capital repatriation post-tax",
            "Multi-currency operating accounts",
            "Tax credit recognition for double-taxation treaty countries (USA, Australia, NZ, UK)",
            "Investor permit + residency pathway included",
        ],
    },
];

export default function Tourism() {
    return (
        <div data-testid="tourism-page" className="bg-background min-h-screen">
            <HeroSection />
            <ProjectTypesSection />
            <IncentivesSection />
            <ROICalculatorSection />
            <ListingsTeaserSection />
            <CTASection />
        </div>
    );
}

function HeroSection() {
    return (
        <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-24 lg:py-32">
            <div className="absolute -top-32 -right-32 h-[500px] w-[500px] rounded-full bg-coral/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full bg-turquoise/12 blur-3xl pointer-events-none" />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                    <Sparkles className="h-3.5 w-3.5 text-coral" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                        Up to 25-year tax holidays · Foreign equity welcome
                    </span>
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] mb-6 max-w-4xl">
                    Fiji tourism is the
                    <br />
                    <span className="italic text-turquoise">most tax-advantaged</span>
                    <br />
                    investment in the South Pacific.
                </h1>
                <p className="font-app text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-3xl mb-10">
                    Resorts. Eco-lodges. Subdivisions. Destination developments. We help foreign capital
                    capture every concession the Fijian government offers — from 25-year income tax holidays
                    under the Tourism Hotel Aid Act to fast-track SLIP approvals and capital-repatriation
                    pathways the moment your project is operational.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <Button size="lg" asChild data-testid="tourism-cta-roi" className="bg-coral hover:bg-coral-deep text-white text-base px-8 h-14">
                        <a href="#roi-calculator">
                            Open ROI calculator <Calculator className="h-4 w-4 ml-2" />
                        </a>
                    </Button>
                    <Button size="lg" asChild variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white text-base px-8 h-14">
                        <Link to="/marketplace?zoning=Tourism" data-testid="tourism-cta-listings">
                            Browse tourism-zoned listings <ArrowRight className="h-4 w-4 ml-2" />
                        </Link>
                    </Button>
                </div>
                <div className="grid sm:grid-cols-4 gap-4 mt-12">
                    {[
                        { stat: "25y", label: "Max tax holiday" },
                        { stat: "100%", label: "Capex write-off" },
                        { stat: "0%", label: "Outer-island tax rate" },
                        { stat: "FJ$250k+", label: "Min project size" },
                    ].map((m) => (
                        <div key={m.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                            <div className="font-display text-2xl lg:text-3xl font-bold text-turquoise tabular-nums">{m.stat}</div>
                            <div className="text-xs uppercase tracking-wider text-white/70 font-app mt-1">{m.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ProjectTypesSection() {
    return (
        <section className="py-16 lg:py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground">Four ways foreign capital wins in Fiji tourism.</h2>
                <p className="font-app text-base text-muted-foreground mb-10 max-w-2xl">
                    Every project type qualifies for a different incentive stack. Pick the one closest to your thesis — we'll structure the rest.
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {PROJECT_TYPES.map((p) => (
                        <Card key={p.slug} data-testid={`tourism-type-${p.slug}`} className="p-6 bg-card hover:border-coral/50 transition group">
                            <div className="h-12 w-12 rounded-lg bg-coral/10 grid place-items-center mb-4 group-hover:bg-coral/20 transition">
                                <p.icon className="h-6 w-6 text-coral" />
                            </div>
                            <h3 className="font-display text-lg font-bold mb-2 text-foreground leading-snug">{p.title}</h3>
                            <p className="font-app text-sm text-muted-foreground leading-relaxed mb-4">{p.body}</p>
                            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-app font-bold text-fiji-green bg-fiji-green/10 px-2 py-1 rounded">
                                <Award className="h-3 w-3" /> {p.tag}
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    );
}

function IncentivesSection() {
    return (
        <section className="py-16 lg:py-20 bg-secondary/40 dark:bg-card/40">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/10 border border-coral/30 mb-4">
                    <Banknote className="h-3.5 w-3.5 text-coral" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">Tax incentive stack</span>
                </div>
                <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground">Fiji's tourism incentive map.</h2>
                <p className="font-app text-base text-muted-foreground mb-10 max-w-3xl">
                    Most foreign investors don't know these exist. Many that do don't know how to stack them. Here's the full map — and the right service stack to capture every concession.
                </p>
                <div className="grid md:grid-cols-2 gap-5">
                    {INCENTIVES.map((inc) => (
                        <Card key={inc.title} data-testid={`tourism-incentive-${inc.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="p-6 bg-card">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-app font-bold text-coral mb-2">{inc.eyebrow}</div>
                            <h3 className="font-display text-xl font-bold mb-3 text-foreground leading-tight">{inc.title}</h3>
                            <p className="font-app text-sm text-muted-foreground leading-relaxed mb-4">{inc.body}</p>
                            <ul className="space-y-2">
                                {inc.bullets.map((b, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm font-app text-foreground/85">
                                        <CheckCircle2 className="h-4 w-4 text-fiji-green mt-0.5 shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ))}
                </div>
                <div className="mt-8 text-center">
                    <p className="font-app text-xs text-muted-foreground italic max-w-2xl mx-auto">
                        Incentive amounts and eligibility thresholds are summarised for guidance only. Actual application depends on project specifics, location, capex level, and current Fiji Revenue & Customs Service interpretations. We confirm exact eligibility during your discovery call.
                    </p>
                </div>
            </div>
        </section>
    );
}

function ROICalculatorSection() {
    const [keys, setKeys] = useState(20);
    const [adr, setAdr] = useState(450);  // USD per night
    const [occupancy, setOccupancy] = useState(65);  // %
    const [opMargin, setOpMargin] = useState(38);  // EBITDA margin %
    const [capex, setCapex] = useState(2_500_000);  // USD total project cost
    const [taxHoliday, setTaxHoliday] = useState(15);  // years

    const calc = useMemo(() => {
        const annualRevenue = keys * adr * 365 * (occupancy / 100);
        const annualEbitda = annualRevenue * (opMargin / 100);
        const annualCorporateTaxFiji = 0.20;  // 20% standard
        // With tax holiday, no income tax for `taxHoliday` years
        const yearOneNetEarnings = annualEbitda;  // tax holiday — keep all EBITDA
        const yearOneNoTaxHoliday = annualEbitda * (1 - annualCorporateTaxFiji);
        const taxSavingsAnnual = yearOneNetEarnings - yearOneNoTaxHoliday;
        const cashOnCash = (yearOneNetEarnings / capex) * 100;
        const paybackYears = capex / yearOneNetEarnings;
        const tenYearCumulative = yearOneNetEarnings * Math.min(taxHoliday, 10);
        return {
            annualRevenue, annualEbitda, yearOneNetEarnings, yearOneNoTaxHoliday,
            taxSavingsAnnual, cashOnCash, paybackYears, tenYearCumulative,
        };
    }, [keys, adr, occupancy, opMargin, capex, taxHoliday]);

    const fmtUSD = (v) => `USD ${Math.round(v).toLocaleString()}`;

    return (
        <section id="roi-calculator" className="py-16 lg:py-20" data-testid="tourism-roi-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fiji-green/10 border border-fiji-green/30 mb-4">
                    <Calculator className="h-3.5 w-3.5 text-fiji-green" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-fiji-green">Interactive · USD</span>
                </div>
                <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground">Tourism ROI calculator.</h2>
                <p className="font-app text-base text-muted-foreground mb-10 max-w-3xl">
                    See how the tax holiday transforms a Fijian resort's economics. All values shown in USD. Adjust the sliders to model your project.
                </p>

                <div className="grid lg:grid-cols-2 gap-6">
                    {/* INPUT SLIDERS */}
                    <Card className="p-6 bg-card space-y-6">
                        <SliderInput
                            label="Number of keys (rooms)" testid="roi-keys"
                            value={keys} onChange={setKeys} min={5} max={150} step={1}
                            displayValue={`${keys} keys`}
                        />
                        <SliderInput
                            label="Average daily rate (ADR)" testid="roi-adr"
                            value={adr} onChange={setAdr} min={100} max={2500} step={25}
                            displayValue={fmtUSD(adr) + " / night"}
                        />
                        <SliderInput
                            label="Occupancy rate" testid="roi-occupancy"
                            value={occupancy} onChange={setOccupancy} min={30} max={90} step={1}
                            displayValue={`${occupancy}%`}
                        />
                        <SliderInput
                            label="Operating margin (EBITDA)" testid="roi-margin"
                            value={opMargin} onChange={setOpMargin} min={15} max={55} step={1}
                            displayValue={`${opMargin}%`}
                        />
                        <SliderInput
                            label="Total project cost (capex)" testid="roi-capex"
                            value={capex} onChange={setCapex} min={250_000} max={50_000_000} step={50_000}
                            displayValue={fmtUSD(capex)}
                        />
                        <SliderInput
                            label="Tax holiday duration (years)" testid="roi-holiday"
                            value={taxHoliday} onChange={setTaxHoliday} min={0} max={25} step={1}
                            displayValue={`${taxHoliday} years`}
                            hint="Up to 25 years for qualifying outer-island Tourism Hotel Aid Act projects"
                        />
                    </Card>

                    {/* OUTPUTS */}
                    <Card className="p-6 bg-gradient-to-br from-coral via-coral to-coral-deep text-white">
                        <div className="flex items-center gap-2 mb-5">
                            <TrendingUp className="h-5 w-5" />
                            <h3 className="font-display text-xl font-bold">Your project numbers</h3>
                        </div>
                        <div className="space-y-4">
                            <OutputRow label="Annual revenue" value={fmtUSD(calc.annualRevenue)} testid="roi-out-revenue" />
                            <OutputRow label="Annual EBITDA" value={fmtUSD(calc.annualEbitda)} testid="roi-out-ebitda" />
                            <div className="pt-2 border-t border-white/20" />
                            <OutputRow label="Year-1 net earnings (with tax holiday)" value={fmtUSD(calc.yearOneNetEarnings)} highlight testid="roi-out-with-holiday" />
                            <OutputRow label="Year-1 net (without holiday)" value={fmtUSD(calc.yearOneNoTaxHoliday)} muted testid="roi-out-without-holiday" />
                            <OutputRow label="Annual tax savings" value={fmtUSD(calc.taxSavingsAnnual)} testid="roi-out-savings" />
                            <div className="pt-2 border-t border-white/20" />
                            <OutputRow label="Cash-on-cash return" value={`${calc.cashOnCash.toFixed(1)}%`} testid="roi-out-cashcash" />
                            <OutputRow label="Capex payback period" value={`${calc.paybackYears.toFixed(1)} years`} testid="roi-out-payback" />
                            <OutputRow label="10-year cumulative earnings" value={fmtUSD(calc.tenYearCumulative)} testid="roi-out-cumulative" highlight />
                        </div>
                        <div className="mt-6 pt-5 border-t border-white/20 text-xs font-app italic opacity-90">
                            Indicative model. Real projects vary by site, brand, seasonality, and operating model. We refine these numbers in your discovery call with your specific land + concept inputs.
                        </div>
                    </Card>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                    <Button size="lg" asChild data-testid="roi-cta-services" className="bg-ocean-green hover:bg-ocean text-white">
                        <Link to="/services">Request feasibility study <ChevronRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                    <Button size="lg" asChild variant="outline" data-testid="roi-cta-invest">
                        <Link to="/invest">Take the investor quiz <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                </div>
            </div>
        </section>
    );
}

function SliderInput({ label, value, onChange, min, max, step, displayValue, hint, testid }) {
    return (
        <div data-testid={testid}>
            <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-app font-semibold text-foreground">{label}</Label>
                <span className="text-sm font-display font-bold tabular-nums text-coral">{displayValue}</span>
            </div>
            <Slider
                value={[value]}
                onValueChange={([v]) => onChange(v)}
                min={min}
                max={max}
                step={step}
                className="mt-2"
            />
            {hint && <p className="text-xs text-muted-foreground font-app mt-1.5 italic">{hint}</p>}
        </div>
    );
}

function OutputRow({ label, value, highlight, muted, testid }) {
    return (
        <div className="flex items-baseline justify-between" data-testid={testid}>
            <span className={`font-app text-sm ${muted ? "opacity-60" : ""}`}>{label}</span>
            <span className={`font-display tabular-nums ${highlight ? "text-2xl font-bold" : muted ? "text-base opacity-70 line-through" : "text-lg font-semibold"}`}>{value}</span>
        </div>
    );
}

function ListingsTeaserSection() {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/listings?zoning=Tourism&limit=6")
            .then(({ data }) => setItems(data.items || []))
            .catch(() => setItems([]));
    }, []);

    return (
        <section className="py-16 lg:py-20 bg-secondary/40 dark:bg-card/40">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                    <div>
                        <h2 className="font-display text-3xl lg:text-4xl font-bold mb-2 text-foreground">Tourism-zoned land available now.</h2>
                        <p className="font-app text-base text-muted-foreground">Live listings from our verified marketplace — pre-screened for tourism use.</p>
                    </div>
                    <Button asChild variant="outline" data-testid="tourism-listings-view-all">
                        <Link to="/marketplace?zoning=Tourism">View all <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    </Button>
                </div>

                {items === null ? (
                    <div className="text-center py-12 font-app text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                    <Card className="p-12 text-center bg-card">
                        <Hotel className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                        <p className="font-app text-sm text-muted-foreground">No tourism-zoned listings live yet — we'll surface them here as they're added.</p>
                        <Button asChild className="mt-4 bg-coral hover:bg-coral-deep text-white">
                            <Link to="/services">Tell us what you're looking for</Link>
                        </Button>
                    </Card>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.slice(0, 6).map((l) => (
                            <Link
                                key={l.id}
                                to={`/properties/${l.id}`}
                                data-testid={`tourism-listing-${l.id}`}
                                className="block bg-card border border-border rounded-lg overflow-hidden hover:border-coral/50 hover:shadow-lg transition group"
                            >
                                <div className="h-40 bg-secondary/60 grid place-items-center text-muted-foreground/50 overflow-hidden">
                                    {l.external_photos?.[0] || l.photos?.[0] ? (
                                        <img src={l.external_photos?.[0] || `${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                                    ) : (
                                        <Hotel className="h-10 w-10" />
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="text-[10px] uppercase tracking-wider text-coral font-app font-bold mb-1.5">Tourism · {l.location_island}</div>
                                    <div className="font-display text-base font-bold mb-2 text-foreground line-clamp-1 group-hover:text-coral transition">{l.title}</div>
                                    <div className="flex items-center justify-between text-sm font-app">
                                        <span className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{l.location_region}</span>
                                        <span className="font-bold tabular-nums text-foreground">{fmtFJD(l.price_fjd)}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function CTASection() {
    return (
        <section className="py-16 lg:py-24">
            <div className="max-w-4xl mx-auto px-4 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fiji-green/10 border border-fiji-green/30 mb-5">
                    <Target className="h-3.5 w-3.5 text-fiji-green" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-fiji-green">Ready to model your project?</span>
                </div>
                <h2 className="font-display text-3xl lg:text-5xl font-bold mb-5 text-foreground">
                    Your numbers, your land, your timeline.
                </h2>
                <p className="font-app text-base lg:text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                    Take the investor quiz, request a feasibility study, or book a 30-minute strategy call. Whatever pace fits — we meet you there.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button size="lg" asChild className="bg-coral hover:bg-coral-deep text-white">
                        <Link to="/invest">Take the quiz <ArrowRight className="h-4 w-4 ml-2" /></Link>
                    </Button>
                    <Button size="lg" asChild variant="outline">
                        <a href="https://cal.com/alvinkumar/general-consultation?utm_source=bularealestatefiji&utm_medium=tourism_page" target="_blank" rel="noopener noreferrer">
                            <Calendar className="h-4 w-4 mr-2" />Book a strategy call
                        </a>
                    </Button>
                </div>
            </div>
        </section>
    );
}
